// System Config — admin UI for env-var runtime overrides.
//
// Shipped 2026-10-15. Edits a Mongo-persisted singleton (SystemConfig
// .envOverrides) that takes precedence over process.env values coming
// from config/defaults.env and the Render dashboard. Scope: ONLY keys
// in TUNABLE_ENV_KEYS (backend allowlist) are listed/editable — the
// backend 400s any PATCH against a key outside the allowlist, so this
// UI can't accidentally expose MONGO_URI, JWT_SECRET, etc.
//
// Precedence per key at runtime:
//   1. Override (DB)  — what you edit here
//   2. Env (process.env from defaults.env + Render dashboard)
//   3. null (consumer-specific fallback — typically a hardcoded default)

import { useCallback, useEffect, useState } from 'react';
import {
  Alert, AlertIcon, Badge, Box, Button, Card, CardBody, Code, HStack,
  Input, Skeleton, Table, Tbody, Td, Text, Th, Thead, Tr, VStack, useToast,
} from '@chakra-ui/react';
import { PageHeader } from '../../shell/PageHeader';
import { apiJson, isApiError } from '../../auth/apiFetch';
import { useAuth } from '../../auth/AuthContext';

type ConfigKey = {
  key:       string;
  override:  string | null;
  env:       string | null;
  effective: string | null;
  source:    'override' | 'env' | 'none';
};

type HistoryEntry = {
  key:       string;
  oldValue:  string | null;
  newValue:  string | null;
  actor:     string | null;
  changedAt: string;
};

type ListResponse = {
  ok:      boolean;
  keys:    ConfigKey[];
  history: HistoryEntry[];
};

const SOURCE_COLOR: Record<ConfigKey['source'], string> = {
  override: 'purple',
  env:      'blue',
  none:     'gray',
};

type Props = {
  // Passed only by the /v2/settings System Config tab. /admin/system-config
  // omits it and keeps the trunk empty-list render.
  showEmptyAllowlist?: boolean;
};

export function AdminSystemConfigPage({ showEmptyAllowlist = false }: Props = {}) {
  const auth = useAuth();
  const toast = useToast();
  const jwtIsAdmin = auth.status === 'authenticated' && auth.user.isSuperAdmin === true;

  const [data, setData] = useState<ListResponse | null>(null);
  const [loading, setLoading] = useState(jwtIsAdmin);
  const [forbidden, setForbidden] = useState(!jwtIsAdmin);
  const [error, setError] = useState<string | null>(null);
  const [drafts, setDrafts] = useState<Record<string, string>>({});
  const [savingKey, setSavingKey] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!jwtIsAdmin) return;
    setLoading(true);
    try {
      const r = await apiJson<ListResponse>('/api/admin/system-config');
      setData(r);
      setError(null);
      setForbidden(false);
      // Seed draft inputs with the current override (so edit-in-place works).
      const seed: Record<string, string> = {};
      for (const k of r.keys) seed[k.key] = k.override ?? '';
      setDrafts(seed);
    } catch (err: unknown) {
      if (isApiError(err) && err.status === 403) {
        setForbidden(true);
        setData(null);
      } else {
        setError(err instanceof Error ? err.message : 'load failed');
      }
    } finally {
      setLoading(false);
    }
  }, [jwtIsAdmin]);

  useEffect(() => { void load(); }, [load]);

  const save = useCallback(async (key: string) => {
    const value = drafts[key] ?? '';
    setSavingKey(key);
    try {
      await apiJson<{ ok: boolean; key: string; oldValue: string | null; newValue: string | null }>(
        `/api/admin/system-config/${encodeURIComponent(key)}`,
        {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ value: value === '' ? null : value }),
        }
      );
      toast({ title: 'Override saved', description: `${key} = ${value || '(cleared)'}`, status: 'success', duration: 3000 });
      await load();
    } catch (err: unknown) {
      toast({
        title: 'Save failed',
        description: err instanceof Error ? err.message : 'Unknown error',
        status: 'error', duration: 5000,
      });
    } finally {
      setSavingKey(null);
    }
  }, [drafts, toast, load]);

  if (forbidden) {
    return (
      <Box>
        <PageHeader title="System config" description="Runtime env-var overrides." />
        <Alert status="warning" borderRadius="md">
          <AlertIcon />
          <Text fontSize="sm">Restricted to super-admins.</Text>
        </Alert>
      </Box>
    );
  }

  return (
    <Box>
      <PageHeader
        title="System config"
        description="Runtime overrides for a curated set of env vars. Edits persist to Mongo and take effect within ~15 seconds without a code deploy or Render restart. Secrets (API tokens, DB URIs) are NOT exposed here."
      />
      <VStack align="stretch" spacing={4}>
        <Card variant="outline" bg="purple.50" borderColor="purple.200">
          <CardBody>
            <Text fontSize="sm" color="purple.900">
              <strong>Precedence:</strong> override (DB, edited here) → env (defaults.env + Render dashboard) → null.
              An empty override field clears the override — runtime falls back to env.
              Secrets are intentionally NOT in the allowlist — change those in Render dashboard.
            </Text>
          </CardBody>
        </Card>

        {loading && !data && (
          <VStack align="stretch" spacing={2}>
            <Skeleton height="60px" />
            <Skeleton height="60px" />
            <Skeleton height="60px" />
          </VStack>
        )}

        {error && (
          <Alert status="error" borderRadius="md">
            <AlertIcon />
            <Text fontSize="sm">{error}</Text>
          </Alert>
        )}

        {showEmptyAllowlist && data && !error && data.keys.length === 0 && (
          <Card variant="outline" data-testid="system-config-empty">
            <CardBody>
              <Text fontSize="sm" color="brand.muted" textAlign="center">
                No tunable keys are in the allowlist.
              </Text>
            </CardBody>
          </Card>
        )}

        {data && data.keys.length > 0 && (
          <Card variant="outline">
            <CardBody p={0}>
              <Table size="sm" variant="simple">
                <Thead>
                  <Tr>
                    <Th>Key</Th>
                    <Th>Effective</Th>
                    <Th>Source</Th>
                    <Th>Env (file / Render)</Th>
                    <Th>Override (edit)</Th>
                    <Th></Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {data.keys.map((k) => {
                    const draft = drafts[k.key] ?? '';
                    const changed = draft !== (k.override ?? '');
                    return (
                      <Tr key={k.key}>
                        <Td>
                          <Code fontSize="xs">{k.key}</Code>
                        </Td>
                        <Td>
                          <Text fontSize="xs" fontFamily="mono" noOfLines={1} maxW="140px" title={k.effective || ''}>
                            {k.effective || <Text as="span" color="brand.muted">(unset)</Text>}
                          </Text>
                        </Td>
                        <Td>
                          <Badge colorScheme={SOURCE_COLOR[k.source]} fontSize="9px">{k.source}</Badge>
                        </Td>
                        <Td>
                          <Text fontSize="xs" fontFamily="mono" color="brand.muted" noOfLines={1} maxW="140px" title={k.env || ''}>
                            {k.env || '—'}
                          </Text>
                        </Td>
                        <Td>
                          <Input
                            size="xs"
                            value={draft}
                            onChange={(e) => setDrafts((d) => ({ ...d, [k.key]: e.target.value }))}
                            placeholder={k.env || '(empty)'}
                            fontFamily="mono"
                            maxW="220px"
                          />
                        </Td>
                        <Td>
                          <Button
                            size="xs"
                            colorScheme="purple"
                            onClick={() => save(k.key)}
                            isLoading={savingKey === k.key}
                            isDisabled={!changed}
                            variant={changed ? 'solid' : 'outline'}
                          >
                            {draft === '' && k.override != null ? 'Clear' : 'Save'}
                          </Button>
                        </Td>
                      </Tr>
                    );
                  })}
                </Tbody>
              </Table>
            </CardBody>
          </Card>
        )}

        {data && data.history.length > 0 && (
          <Card variant="outline">
            <CardBody>
              <Text fontSize="xs" fontWeight="700" color="brand.muted" textTransform="uppercase" letterSpacing="0.05em" mb={2}>
                Recent history ({data.history.length})
              </Text>
              <VStack align="stretch" spacing={1}>
                {data.history.slice(0, 15).map((h, i) => (
                  <HStack key={i} spacing={2} fontSize="11px" color="brand.muted" wrap="wrap">
                    <Text>{new Date(h.changedAt).toLocaleString()}</Text>
                    <Code fontSize="10px">{h.key}</Code>
                    <Text>→</Text>
                    <Code fontSize="10px">{h.newValue == null ? '(cleared)' : h.newValue}</Code>
                    {h.actor && <Text>· by {h.actor}</Text>}
                  </HStack>
                ))}
              </VStack>
            </CardBody>
          </Card>
        )}
      </VStack>
    </Box>
  );
}

export default AdminSystemConfigPage;
